
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Calendar, User, ArrowLeft, Tag, ArrowRight } from 'lucide-react';

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { posts, settings } = useData();

  // Robust ID comparison (handle number vs string from storage)
  const post = posts.find(p => String(p.id) === String(id));

  if (!post) {
    return (
      <div className="bg-white min-h-screen pt-24 pb-12 flex flex-col items-center justify-center text-center px-4">
        <h2 className="font-display text-3xl font-bold text-gray-900 mb-4 uppercase">Post Not Found</h2>
        <p className="text-gray-600 mb-8 text-lg">The article you are looking for could not be found.</p>
        <Link
          to="/blog"
          className="inline-flex items-center text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider transition-transform hover:-translate-y-0.5 shadow-lg"
          style={{ backgroundColor: settings.logoColor }}
        >
          <ArrowLeft size={18} className="mr-2" /> Back to News
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-24 pb-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Navigation */}
        <div className="mb-8">
          <Link to="/blog" className="inline-flex items-center text-gray-500 hover:text-automax-red transition-colors font-medium">
            <ArrowLeft size={18} className="mr-2" /> Back to News
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
            <span
              style={{ color: settings.logoColor, borderColor: settings.logoColor }}
              className="border px-3 py-1 rounded-sm font-bold uppercase tracking-wider"
            >
              {post.category}
            </span>
            <div className="flex items-center text-gray-500">
              <Calendar size={16} className="mr-2" />
              {new Date(post.date).toLocaleDateString()}
            </div>
            <div className="flex items-center text-gray-500">
              <User size={16} className="mr-2" />
              {post.author}
            </div>
          </div>

          <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 uppercase">
            {post.title}
          </h1>
        </div>

        {/* Featured Image */}
        <div className="rounded-sm overflow-hidden shadow-lg mb-12 h-64 md:h-96">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none text-gray-800 prose-red prose-headings:font-display prose-headings:uppercase prose-img:rounded-sm">
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>

        {/* Share / Footer */}
        <div className="mt-16 pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 text-gray-500 mb-4 md:mb-0">
            <Tag size={18} />
            <span className="font-medium">Category: {post.category}</span>
          </div>

          <div className="flex gap-4">
            <button className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-sm text-sm font-bold transition-colors">
              Share Article
            </button>
          </div>
        </div>
      </article>

      {/* Read Next (Simulated) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <h3 className="font-display text-3xl font-bold text-gray-900 mb-8 border-l-4 pl-4" style={{ borderColor: settings.logoColor }}>Read Next</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.filter(p => p.id !== post.id).slice(0, 2).map(p => (
            <Link key={p.id} to={`/blog/${p.id}`} className="group block bg-gray-50 rounded-sm p-4 hover:bg-gray-100 transition-colors">
              <h4 className="font-bold text-xl text-gray-900 group-hover:text-automax-red transition-colors mb-2">{p.title}</h4>
              <p className="text-gray-500 text-sm line-clamp-2">{p.excerpt}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
